import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

type ImageProps = {
  src: string;
  alt: string;
  resX: number;
  resY: number;
  className?: string;
  png?: boolean;
  style?: React.CSSProperties;
};

type ImageOptions = {
  bucket: string;
  key: string | undefined;
  edits: {
    resize: {
      fit: string;
      background: {
        r: number;
        g: number;
        b: number;
        alpha: number;
      };
      width?: number;
      height?: number;
    };
    toFormat?: string;
  };
};

function Image({
  src,
  alt,
  resX,
  resY,
  className,
  png,
  style = {},
}: ImageProps) {
  const placeHolderImage = `https://assets.flaivy.com/${resX}x${resY}/b3f2834cf763bdb714f48eb3e8e59723.png`;
  const aspectRatio = resX && resY ? resX / resY : undefined;
  if (src === 'NA') {
    const optionsa = {
      bucket: 'flaivy-assets',
      key: 'b3f2834cf763bdb714f48eb3e8e59723.png',
      edits: {
        tint: {
          r: 255,
          g: 1,
          b: 1,
        },
      },
    };

    const naImage = Buffer.from(JSON.stringify(optionsa)).toString('base64');
    return (
      <img
        src={`https://assets.flaivy.com/${naImage}`}
        alt={alt}
        className={className}
        height={resY}
        style={{
          aspectRatio,
          width: `${resX}px`,
          height: 'auto',
          ...style,
        }}
        width={resX}
      />
    );
  }

  if (!src)
    return (
      <img
        src={placeHolderImage}
        alt={alt}
        height={resY}
        width={resX}
        style={{
          aspectRatio,
          width: `${resX}px`,
          height: 'auto',
          ...style,
        }}
        className={className}
      />
    );
  if (!_.includes(src, 'assets.flaivy.com/')) {
    return (
      <img
        src={src}
        alt={alt}
        height={resY}
        width={resX}
        style={{
          aspectRatio,
          width: `${resX}px`,
          height: 'auto',
          ...style,
        }}
        className={className}
      />
    );
  }
  if (_.endsWith(src, '.svg')) {
    return (
      <img
        src={src}
        alt={alt}
        height={resY}
        width={resX}
        style={{
          aspectRatio,
          width: `${resX}px`,
          height: 'auto',
          ...style,
        }}
        className={className}
      />
    );
  }
  if (_.endsWith(src, '.gif')) {
    const gifSrc = src.replace(
      'assets.flaivy.com',
      'flaivy-assets.s3.eu-north-1.amazonaws.com',
    );

    return (
      <img
        src={gifSrc}
        alt={alt}
        height={resY}
        width={resX}
        style={{
          aspectRatio,
          width: `${resX}px`,
          height: 'auto',
          ...style,
        }}
        className={className}
      />
    );
  }
  // https://sharp.pixelplumbing.com/api-resize
  const key = src.split('/').pop();

  const imageOptions: ImageOptions = {
    bucket: 'flaivy-assets',
    key,
    edits: {
      resize: {
        fit: 'contain',
        background: { r: 255, g: 255, b: 255, alpha: 0.5 },
      },
    },
  };

  if (resX) {
    imageOptions.edits.resize.width = resX;
  }

  if (resY) {
    imageOptions.edits.resize.height = resY;
  }

  if (png) {
    imageOptions.edits.toFormat = 'png';
  }

  const imageOptionBase64 = Buffer.from(JSON.stringify(imageOptions)).toString(
    'base64',
  );
  return (
    <img
      src={`https://assets.flaivy.com/${imageOptionBase64}`}
      style={{
        aspectRatio,
        width: `${resX}px`,
        height: 'auto',
        ...style,
      }}
      alt={alt}
      className={className}
      height={resY}
      width={resX}
    />
  );
}

Image.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  png: PropTypes.bool,
  resX: PropTypes.number,
  resY: PropTypes.number.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
};

Image.defaultProps = {
  src: undefined,
  alt: '..',
  className: '',
  resX: undefined,
  png: false,
  style: undefined,
};
export default Image;
